<template>
	<v-container
		class="grid-flex-container my-0 py-5"
		style="overflow-y: hidden"
		fluid
	>
		<div class="d-flex">
			<v-text-field
				v-model="searchReports"
				class="py-0 mr-2"
				append-icon="mdi-magnify"
				label="Search"
				single-line
				hide-details
			/>
			<export-modal
				:show-time-zone-options="true"
				:show-execution-options="true"
				:show-data-scope-options="false"
				:disabled="!canExport"
				@exportModalEvent="onExportModalEvent"
			>
				<template #activator="{ on, attrs }">
					<v-btn
						v-bind="attrs"
						v-on="on"
						color="primary"
						:disabled="!canExport"
					>
						<v-icon class="mr-1">mdi-cloud-download</v-icon>
						Export
					</v-btn>
				</template>

				<template #custom-options="{ exportConfig }">
					<v-divider></v-divider>

					<v-list-item
						@click.prevent="exportSingleFile(exportConfig)"
					>
						<v-list-item-content>
							<v-list-item-title
								>Export single file</v-list-item-title
							>
						</v-list-item-content>
					</v-list-item>

					<v-divider v-if="splitBy.length > 0"></v-divider>

					<v-list-item v-if="splitBy.length > 0">
						<v-list-item-content>
							<v-text-field
								v-model="searchSplitBy"
								label="Search split by options"
								append-icon="mdi-magnify"
								dense
								clearable
								hide-details
							></v-text-field>
						</v-list-item-content>
					</v-list-item>

					<v-list-item
						v-if="
							splitBy.length > 0 && filteredSplitBy.length === 0
						"
					>
						<v-list-item-content>
							<div class="text-center grey--text text--darken-1">
								No split by options available
							</div>
						</v-list-item-content>
					</v-list-item>

					<v-virtual-scroll
						v-if="splitBy.length > 0 && filteredSplitBy.length >= 0"
						:items="filteredSplitBy"
						height="200"
						:itemHeight="40"
					>
						<template v-slot:default="{ item }">
							<v-list-item
								@click="exportWithSplitBy(item, exportConfig)"
							>
								<v-list-item-content>
									<v-list-item-title>{{
										item
									}}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</template>
					</v-virtual-scroll>
				</template>
			</export-modal>
		</div>

		<div class="d-flex flex-wrap mt-3">
			<template v-if="selectedReports.length > 0">
				<v-chip-group>
					<v-chip
						v-for="(report, index) in selectedReports"
						:key="report.Id"
						close
						@click:close="removeReport(index)"
						>{{ report.ReportName }}</v-chip
					>
				</v-chip-group>
			</template>
			<template v-else>
				<div
					class="d-flex justify-center align-center grey--text text--darken-1"
					style="width: 100%; min-height: 48px"
				>
					No item selected. Please select an item to proceed...
				</div>
			</template>
		</div>

		<div class="generic-grid-container mt-2">
			<v-data-table
				ref="dataTable"
				dense
				hide-default-footer
				disable-pagination
				fixed-header
				loading-text="Loading..."
				:items="reports"
				:headers="reportsHeaders"
				:search.sync="searchReports"
				:loading="loadingReports"
				group-by="WorkbookName"
				show-select
				:single-select="false"
				v-model="selectedReports"
				item-key="Id"
			>
				<template v-slot:[`header.data-table-select`]></template>

				<template v-slot:[`group.header`]="{ group, toggle, isOpen }">
					<td
						:colspan="reportsHeaders.length + 1"
						@click="onGroupToggle(toggle)"
						style="cursor: pointer"
						:ref="'dataTable_' + group"
					>
						<div class="d-flex align-center">
							<v-icon v-if="!isOpen">mdi-plus</v-icon>
							<v-icon v-else>mdi-minus</v-icon>
							<span class="text-subtitle-2 ml-2">{{
								group || 'All Reports'
							}}</span>
						</div>
					</td>
				</template>

				<template v-slot:[`item.Type`]="{ item }">
					<span v-if="reportHasCustomData(item)"> Custom Data </span>
					<span v-else> Default Data </span>
				</template>

				<template v-slot:[`item.DataSource`]="{ item }">
					{{ reportDataSource(item) }}
				</template>
			</v-data-table>
		</div>
	</v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { DataExportsGetters } from '../../../store/getters';
import { DataExportsActions } from '../../../store/actions';
import { reportTypes } from '../../../enums/reportTypes';
import Snackbar from '../../core/dialogs/snackbarMessage';
import ExportModal from '../../shared/export/ExportModal.vue';

export default {
	name: 'DataExport',
	components: {
		ExportModal,
	},
	data() {
		return {
			searchReports: null,
			selectedReports: [],
			reportsHeaders: [
				{ text: '', value: 'selection', sortable: false },
				{ text: 'Report Name', value: 'ReportName' },
				{ text: 'Report Type', value: 'Type' },
				{ text: 'Data Source', value: 'DataSource' },
			],
			searchSplitBy: '',
			loaded: false,
		};
	},
	watch: {
		reports: {
			handler() {
				this.$nextTick(() => {
					this.collapseGroups();
				});
			},
			immediate: false,
		},
		$refs: {
			handler() {
				this.$nextTick(() => {
					this.collapseGroups();
				});
			},
			immediate: false,
		},
	},
	computed: {
		...mapGetters({
			loadingReports: [DataExportsGetters.LOADING_REPORTS],
			reports: [DataExportsGetters.REPORTS],
		}),
		splitBy() {
			if (!this.selectedReports || this.selectedReports.length === 0) {
				return [];
			}

			// Get the labels from the first report
			const firstReportLabels = new Set(
				this.selectedReports[0].Fields?.map((field) => field.Label) ||
					[]
			);

			// Check if each label is present in all selected reports
			const commonLabels = Array.from(firstReportLabels).filter(
				(label) => {
					return this.selectedReports.every((report) => {
						return report.Fields?.some(
							(field) => field.Label === label
						);
					});
				}
			);

			return commonLabels;
		},
		// New computed properties for export menu
		filteredSplitBy() {
			if (!this.searchSplitBy) {
				return this.splitBy;
			}

			const search = this.searchSplitBy.toLowerCase();
			return this.splitBy.filter((option) =>
				option.toLowerCase().includes(search)
			);
		},
		canExport() {
			return this.selectedReports.length > 0;
		},
	},
	mounted() {
		this.$store.dispatch(DataExportsActions.LOAD);
	},
	destroyed() {
		this.$store.dispatch(DataExportsActions.CLEAR);
	},
	methods: {
		collapseGroups() {
			if (this.reports.length <= 0) return;
			const distinctWorkbookNames = [
				...new Set(this.reports.map((item) => item.WorkbookName)),
			];
			distinctWorkbookNames.forEach((key) => {
				if (key) {
					let refName = 'dataTable_' + key;
					let ref = this.$refs[refName];
					if (ref) {
						ref.click();
					}
				}
			});
			this.$nextTick(() => {
				if (this.$refs['dataTable_']) {
					this.$refs['dataTable_'].click();
				}
			});
		},
		onGroupToggle(toggle) {
			// Get all workbook group names
			const distinctWorkbookNames = [
				...new Set(this.reports.map((item) => item.WorkbookName)),
			];
			const clickedElement = event.currentTarget;

			// First collapse all groups
			distinctWorkbookNames.forEach((key) => {
				// Handle both null and non-null groups
				const refName = 'dataTable_' + (key || '');
				const ref = this.$refs[refName];
				const headerElement = ref?.[0] || ref;

				// Get the current state of the group from its icon
				if (headerElement && headerElement.querySelector) {
					const isOpen = !!headerElement.querySelector('.mdi-minus');

					// If group is open and not the one being clicked, close it
					if (isOpen && headerElement !== clickedElement) {
						headerElement.click();
					}
				}
			});

			// Then toggle the clicked group
			this.selectedReports = [];
			toggle();
		},
		removeReport(index) {
			this.selectedReports.splice(index, 1);
		},
		reportHasCustomData(report) {
			if (!report) return null;
			if (!report.ReportType) return null;
			return report.ReportType == reportTypes.CUSTOM_DATA;
		},
		reportDataSource(report) {
			if (!report) return null;
			if (!report.ReportType) return null;
			if (report.ReportType == reportTypes.CUSTOM_DATA)
				return report.CustomData;
			if (!report.GridView) return null;
			return report.GridView.Name;
		},

		onExportModalEvent(exportConfig) {
			this.exportData(null, exportConfig);
		},

		exportSingleFile(exportConfig) {
			this.exportData(null, exportConfig);
		},

		exportWithSplitBy(splitByOption, exportConfig) {
			this.exportData(splitByOption, exportConfig);
		},

		exportData(splitByOption, exportConfig) {
			const config = {
				Reports: this.selectedReports.map((report) => report.ReportId),
				Timezone: exportConfig.timeZone,
				SplitBy: splitByOption,
				isDirectMode: !exportConfig.isBackgroundExport,
			};
			this.$store
				.dispatch(DataExportsActions.EXPORT, config)
				.then((success) => {
					if (success && exportConfig.isBackgroundExport) {
						Snackbar({
							message:
								'Export request registered. You will receive a notification when the file is ready for download!',
						});
					}
				});
		},
	},
};
</script>
